import { useCallback, useState } from "react";

type CopiedValue = string | null;
type CopyFn = (text: string) => Promise<boolean>;

export function useCopyToClipboard(): [CopiedValue, CopyFn] {
    const [copiedText, setCopiedText] = useState<CopiedValue>(null);

    const fallbackCopyTextToClipboard = (text: string) => {
        const textArea = document.createElement("textarea");
        textArea.value = text;

        // Avoid scrolling to bottom when focus is applied
        textArea.style.position = "fixed";
        textArea.style.left = "-99999px";
        textArea.style.top = "-99999px";

        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            document.execCommand("copy");
            console.log("Fallback: Copying text command was successful");
            document.body.removeChild(textArea);
            return true;
        } catch (err) {
            console.error("Fallback: Unable to copy", err);
            document.body.removeChild(textArea);
            return false;
        }
    };

    const copy: CopyFn = useCallback(async (text) => {
        if (!navigator?.clipboard) {
            console.warn("Clipboard not supported, using fallback method");
            const success = fallbackCopyTextToClipboard(text);
            setCopiedText(success ? text : null);
            return success;
        }

        try {
            await navigator.clipboard.writeText(text);
            setCopiedText(text);
            console.log("Text copied using Clipboard API:", text);
            return true;
        } catch (error) {
            console.warn("Clipboard API failed, using fallback method", error);
            const success = fallbackCopyTextToClipboard(text);
            setCopiedText(success ? text : null);
            return success;
        }
    }, []);

    return [copiedText, copy];
}
